import { useEffect, useMemo, useState, type FormEvent } from 'react';
import type { CreateRoleInput, MenuPermissionDto, RbacLevel, Role } from '@catalyst-psa/shared';
import { MENU_ITEMS, MENU_ITEM_KEYS, RBAC_LEVELS } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/authContext.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { TextField } from '../../components/ui/Field.js';
import { FormActions, useSaveMode } from '../../components/ui/FormActions.js';
import { Modal } from '../../components/ui/Modal.js';
import { useCreateRole, useDeleteRole, useUpdateRole } from './api.js';
import { RBAC_LEVEL_LABELS } from './labels.js';

interface RoleFormModalProps {
  isOpen: boolean;
  role: Role | null;
  onClose: () => void;
}

type MenuLevelMap = Record<string, RbacLevel>;

function emptyMenuLevels(): MenuLevelMap {
  const map: MenuLevelMap = {};
  for (const key of MENU_ITEM_KEYS) map[key] = 'none';
  return map;
}

function toMenuLevelMap(role: Role | null): MenuLevelMap {
  const map = emptyMenuLevels();
  if (role) {
    for (const permission of role.menuPermissions) {
      if (permission.key in map) map[permission.key] = permission.level;
    }
  }
  return map;
}

function fieldErrorsOf(error: unknown): Record<string, string> {
  if (error instanceof ApiError && error.envelope?.error.details) {
    const result: Record<string, string> = {};
    for (const detail of error.envelope.error.details) {
      result[detail.path] = detail.message;
    }
    return result;
  }
  return {};
}

export function RoleFormModal({ isOpen, role: roleProp, onClose }: RoleFormModalProps): JSX.Element {
  const [role, setRole] = useState<Role | null>(roleProp);
  const isEdit = role !== null;
  const { isAdmin } = useAuth();
  const saveMode = useSaveMode();
  const [name, setName] = useState('');
  const [menuLevels, setMenuLevels] = useState<MenuLevelMap>(emptyMenuLevels);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const createMutation = useCreateRole();
  const updateMutation = useUpdateRole();
  const deleteMutation = useDeleteRole();
  const mutation = isEdit ? updateMutation : createMutation;

  const resetCreate = createMutation.reset;
  const resetUpdate = updateMutation.reset;
  const resetSaveMode = saveMode.reset;

  useEffect(() => {
    if (isOpen) {
      setRole(roleProp);
    }
  }, [isOpen, roleProp]);

  useEffect(() => {
    if (isOpen) {
      setName(role?.name ?? '');
      setMenuLevels(toMenuLevelMap(role));
      setConfirmOpen(false);
      resetSaveMode();
      resetCreate();
      resetUpdate();
    }
  }, [isOpen, role, resetSaveMode, resetCreate, resetUpdate]);

  const fieldErrors = fieldErrorsOf(mutation.error);
  const generalError =
    mutation.error instanceof ApiError && !mutation.error.envelope?.error.details?.length
      ? mutation.error.message
      : mutation.isError && !(mutation.error instanceof ApiError)
        ? 'Failed to save role.'
        : null;

  const canDelete = isEdit && isAdmin && !role.isSystem;

  // Menu items grouped by their sidebar section, in catalog order, for the grid.
  const menuBySection = useMemo(() => {
    const order: { section: string; items: typeof MENU_ITEMS }[] = [];
    const index = new Map<string, number>();
    for (const item of MENU_ITEMS) {
      let at = index.get(item.section);
      if (at === undefined) {
        at = order.length;
        index.set(item.section, at);
        order.push({ section: item.section, items: [] });
      }
      order[at]!.items.push(item);
    }
    return order;
  }, []);

  const setLevel = (key: string, level: RbacLevel): void => {
    setMenuLevels((prev) => ({ ...prev, [key]: level }));
  };

  const setAll = (level: RbacLevel): void => {
    setMenuLevels(() => {
      const next: MenuLevelMap = {};
      for (const key of MENU_ITEM_KEYS) next[key] = level;
      return next;
    });
  };

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const menuPermissions: MenuPermissionDto[] = [];
    for (const key of MENU_ITEM_KEYS) {
      const level = menuLevels[key];
      if (level && level !== 'none') menuPermissions.push({ key, level });
    }
    const payload: CreateRoleInput = { name: name.trim(), menuPermissions };

    if (isEdit) {
      updateMutation.mutate(
        { id: role.id, input: payload },
        { onSuccess: () => saveMode.closeAfterSaveRef.current && onClose() },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: (created) => {
          if (saveMode.closeAfterSaveRef.current) {
            onClose();
          } else {
            setRole(created);
          }
        },
      });
    }
  };

  const handleDelete = (): void => {
    if (!role) {
      return;
    }
    deleteMutation.mutate(role.id, {
      onSuccess: () => {
        setConfirmOpen(false);
        onClose();
      },
    });
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isEdit ? 'Edit role' : 'New role'}
        size="lg"
        footer={
          <FormActions
            formId="role-form"
            saveMode={saveMode}
            isPending={mutation.isPending}
            onCancel={onClose}
            leading={
              canDelete ? (
                <Button
                  variant="ghost"
                  className="mr-auto text-rose-600 hover:bg-rose-50"
                  onClick={() => setConfirmOpen(true)}
                  disabled={mutation.isPending || deleteMutation.isPending}
                >
                  Delete
                </Button>
              ) : null
            }
          />
        }
      >
        <form id="role-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
          {generalError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {generalError}
            </div>
          ) : null}

          <TextField
            label="Name"
            required
            value={name}
            maxLength={100}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            error={fieldErrors.name}
          />

          {role?.isSystem ? (
            <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
              This is a system role. You can adjust its permissions, but it cannot be deleted.
            </div>
          ) : null}

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Menu permissions
              </p>
              <div className="flex items-center gap-3 text-xs">
                <button
                  type="button"
                  className="text-brand-600 hover:text-brand-800"
                  onClick={() => setAll('view')}
                >
                  Grant all (view)
                </button>
                <button
                  type="button"
                  className="text-slate-500 hover:text-slate-700"
                  onClick={() => setAll('none')}
                >
                  Clear all
                </button>
              </div>
            </div>
            <p className="mb-3 text-xs text-slate-500">
              Choose the access level for each menu item. A section appears in the sidebar only
              when at least one of its items is set to View, Edit, or Delete. (Admins always see
              every item; some Administration and Integrations pages still require admin access.)
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              {menuBySection.map((group) => (
                <div key={group.section} className="rounded-lg border border-slate-200 p-3">
                  <p className="mb-2 text-xs font-semibold text-slate-600">{group.section}</p>
                  <div className="flex flex-col gap-1.5">
                    {group.items.map((item) => (
                      <div
                        key={item.key}
                        className="flex items-center justify-between gap-2 text-sm text-slate-700"
                      >
                        <span className="truncate">{item.label}</span>
                        <select
                          value={menuLevels[item.key] ?? 'none'}
                          onChange={(e) => setLevel(item.key, e.target.value as RbacLevel)}
                          className="rounded-lg border border-slate-300 px-2 py-1 text-xs focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                        >
                          {RBAC_LEVELS.map((level) => (
                            <option key={level} value={level}>
                              {RBAC_LEVEL_LABELS[level]}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </form>
      </Modal>
      <ConfirmDialog
        isOpen={confirmOpen}
        title="Delete role"
        message={`Delete "${role?.name ?? ''}"? This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        isConfirming={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}
